import { useEffect, useMemo, useRef, useState } from 'react';
import type { FeatureCollection, Point } from 'geojson';

import { fetchBarrierGeometry } from '@/api/endpoints/barriers';
import type {
  DetourWaypointInput,
  DetourWaypointProperties,
} from '@/components/map/detourWaypointsLayer';
import { buildDetourWaypointsCollection } from '@/components/map/detourWaypointsLayer';
import type { VariantCollections } from '@/hooks/useVisibleRoutesGeometry';
import { useTileRevision } from '@/stores/TileRevisionContext';
import type { LatLonPoint } from '@/types/barriers';

/**
 * Builds the detour-waypoints FeatureCollection consumed by the
 * `detours` map layer.
 *
 * Input: the already-loaded `barrier_aware` geometries (which carry
 * their `applied_barriers` list per feature). Output: a
 * FeatureCollection of Points — one dot per `(route_id, barrier_id,
 * auto_waypoint_index)` triple.
 *
 * Barrier geometries are fetched on demand and cached on a module-
 * local `Map` so revisiting the same barrier does not re-fetch. The
 * cache is a simple invalidation-less LRU-ish structure: size is
 * bounded only by the number of barriers the user has ever seen in
 * one session, which in practice is small for an internal tool.
 */
export function useDetourWaypoints(
  collections: VariantCollections,
): FeatureCollection<Point, DetourWaypointProperties> {
  const barrierIds = useMemo(() => {
    const set = new Set<number>();
    for (const feature of collections.barrier_aware.features) {
      const applied = readAppliedBarriers(feature.properties);
      for (const id of applied) set.add(id);
    }
    return set;
  }, [collections]);

  const [cache, setCache] = useState<ReadonlyMap<number, readonly LatLonPoint[]>>(
    new Map(),
  );
  const fetchingRef = useRef<Set<number>>(new Set());
  // Invalidate the per-barrier waypoint cache whenever ANY barrier
  // mutation happens. Without this, a deleted barrier's auto_waypoints
  // would stay visible on the map (the cache lookup short-circuits the
  // fetch that would otherwise return 404 and produce the empty list).
  const { barriersRevision } = useTileRevision();
  useEffect(() => {
    setCache(new Map());
    fetchingRef.current.clear();
  }, [barriersRevision]);

  useEffect(() => {
    const missing: number[] = [];
    for (const id of barrierIds) {
      if (!cache.has(id) && !fetchingRef.current.has(id)) {
        missing.push(id);
        fetchingRef.current.add(id);
      }
    }
    if (missing.length === 0) return;

    let cancelled = false;
    Promise.all(
      missing.map(async (id) => {
        try {
          const feature = await fetchBarrierGeometry(id);
          return [id, feature.properties.auto_waypoints] as const;
        } catch {
          return [id, []] as const;
        }
      }),
    )
      .then((results) => {
        if (cancelled) return;
        setCache((prev) => {
          const next = new Map(prev);
          for (const [id, waypoints] of results) {
            next.set(id, waypoints.map((p) => ({ lat: p.lat, lon: p.lon })));
          }
          return next;
        });
      })
      .finally(() => {
        // Always release in-flight tracking so subsequent renders can
        // re-fetch cancelled barriers. Without this, a component
        // cleanup that fires after `missing` was scheduled but before
        // the Promise resolves would leave ids wedged in
        // `fetchingRef` forever.
        for (const id of missing) fetchingRef.current.delete(id);
      });
    return () => {
      cancelled = true;
    };
  }, [barrierIds, cache]);

  return useMemo(() => {
    const entries: DetourWaypointInput[] = [];
    for (const feature of collections.barrier_aware.features) {
      const applied = readAppliedBarriers(feature.properties);
      const routeId = readRouteId(feature.properties);
      if (routeId === null) continue;
      for (const barrierId of applied) {
        const waypoints = cache.get(barrierId);
        if (!waypoints || waypoints.length === 0) continue;
        entries.push({ routeId, barrierId, waypoints });
      }
    }
    return buildDetourWaypointsCollection(entries);
  }, [collections, cache]);
}

function readAppliedBarriers(props: unknown): readonly number[] {
  if (!props || typeof props !== 'object') return [];
  const raw = (props as { applied_barriers?: unknown }).applied_barriers;
  return Array.isArray(raw) ? raw.filter((n): n is number => typeof n === 'number') : [];
}

function readRouteId(props: unknown): string | null {
  if (!props || typeof props !== 'object') return null;
  const raw = (props as { route_id?: unknown }).route_id;
  return typeof raw === 'string' && raw.length > 0 ? raw : null;
}
